import qs from 'qs';
import moment from 'moment';
import * as XLSX from 'xlsx';
import api from './api';
import generateSearchRequest from '../utils/sequelize-search-builder-client';

export const ORDER_BTC_EXPORT_HEADERS = [
	'ID',
	'ID do cliente',
	'ID da empresa',
	'ID da fatura',
	'ID da assinatura',
	'ID do contrato',
	'ID do entregador',
	'Status',
	'Tipo de recebimento',
	'Tipo de pacote',
	'Tamanho do pacote',
	'Rastreio',
	'Dependente',
	'Destinatário',
	'Documento do destinatário',
	'Ocorrência',
	'Comentários',
	'Valor do pedido',
	'Pagamento',
	'Criação',
	'Retirada',
	'Atualização',
	'Cliente',
	'Telefone',
	'Entregador',
	'Empresa',
	'Unidade',
	'Estoque',
];

const toText = (value) => (value === null || value === undefined ? '' : String(value));

const formatExportDate = (value) => (value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '');

export const buildOrderBtcExportRows = (orders = []) =>
	orders.map((order) => ({
		ID: toText(order.id),
		'ID do cliente': toText(order.clientId),
		'ID da empresa': toText(order.companyId),
		'ID da fatura': toText(order.invoiceId),
		'ID da assinatura': toText(order.subscriptionId),
		'ID do contrato': toText(order.contractId),
		'ID do entregador': toText(order.delivermanId),
		Status: toText(order.status),
		'Tipo de recebimento': toText(order.typeReceive),
		'Tipo de pacote': toText(order.packageType),
		'Tamanho do pacote': toText(order.packageSize),
		Rastreio: toText(order.trackingCode),
		Dependente: toText(order.dependent),
		Destinatário: toText(order.receiverName),
		'Documento do destinatário': toText(order.receiverDocument),
		Ocorrência: toText(order.occurrence),
		Comentários: toText(order.comments),
		'Valor do pedido': toText(order.valueOrder),
		Pagamento: toText(order.payment),
		Criação: formatExportDate(order.createdAt),
		Retirada: formatExportDate(order.deliveryDate),
		Atualização: formatExportDate(order.updatedAt),
		Cliente: toText(order.client?.name),
		Telefone: toText(order.client?.phone),
		Entregador: toText(order.deliverman?.name),
		Empresa: toText(order.company?.name),
		Unidade: toText(order.client?.base?.name),
		Estoque: order.lowerStock ? 'Parte Baixa' : 'Parte Alta',
	}));

export const createOrderBtcWorkbook = (orders = []) => {
	const rows = buildOrderBtcExportRows(orders);
	const worksheet = XLSX.utils.aoa_to_sheet([
		ORDER_BTC_EXPORT_HEADERS,
		...rows.map((row) => ORDER_BTC_EXPORT_HEADERS.map((header) => row[header])),
	]);
	const trackingColumn = ORDER_BTC_EXPORT_HEADERS.indexOf('Rastreio');

	for (let rowIndex = 1; rowIndex <= rows.length; rowIndex += 1) {
		const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: trackingColumn });
		const cell = worksheet[cellAddress];
		if (cell) {
			cell.t = 's';
			cell.z = '@';
		}
	}

	worksheet['!cols'] = ORDER_BTC_EXPORT_HEADERS.map((header) => ({
		wch: Math.min(Math.max(header.length + 2, 14), 28),
	}));

	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, 'Pedidos B2C');
	return workbook;
};

const selectOrder = (order) => {
	return {
		...order,
		createdAt: order.createdAt ? moment(order.createdAt).toDate() : null,
		updatedAt: order.updatedAt ? moment(order.updatedAt).toDate() : null,
		deliveryDate: order.deliveryDate ? moment(order.deliveryDate).toDate() : null,
	};
};

const selectOrders = (orders) => orders.map(selectOrder);

// The table columns show values that live on associated models, so their sorter keys
// have to be translated to the association path the API understands. Sending the raw
// column key makes the API order by a column that does not exist on `ordersBtc`.
const ORDER_FIELD_ALIASES = {
	clientName: 'client.name',
	companyName: 'company.name',
	baseName: 'client.base.name',
	delivermanName: 'deliverman.name',
};

export const normalizeOrder = (order) => {
	if (!order || typeof order !== 'object') return order;

	return Object.fromEntries(
		Object.entries(order).map(([field, direction]) => [
			ORDER_FIELD_ALIASES[field] || field,
			direction,
		]),
	);
};

class OrderBtcService {
	getAll({ page = 0, size = 10, order, filters, search }) {
		//const _order = {};

		// for (const key in order) {
		// 	if (key === 'clientName') {
		// 		_order['client.name'] = order[key];
		// 	} else if (key === 'delivermanName') {
		// 		_order['deliverman.name'] = order[key];
		// 	} else {
		// 		_order[key] = order[key];
		// 	}
		// }

		//console.log('_order', _order);

		const settings = {
			where: {
				id: {
					as: 'id',
					type: 'like',
				},
				clientId: {
					as: 'clientId',
					type: 'equal',
				},
				packageType: {
					as: 'packageType',
					type: 'like',
				},
				packageSize: {
					as: 'packageSize',
					type: 'like',
				},
				trackingCode: {
					as: 'trackingCode',
					type: 'like',
				},
				status: {
					as: 'status',
					type: 'like',
				},
				// Several statuses at once (e.g. the Retiradas pending list: "Aguardando" +
				// "Em rota"), so the API can paginate the combined set itself.
				'status:in': {
					as: 'status',
					type: 'in',
				},
				baseName: {
					as: 'client.base.name',
					type: 'like',
				},
				baseId: {
					as: 'client.base.id',
					type: 'equal',
				},
				delivermanName: {
					as: 'deliverman.name',
					type: 'like',
				},
				'createdAt:between': {
					as: 'createdAt',
					type: 'between',
				},
				'deliveryDate:between': {
					as: 'deliveryDate',
					type: 'between',
				},
			},
			order: normalizeOrder(order),
		};
		const { clientName, ...restFilters } = filters || {};
		const request = generateSearchRequest(restFilters, settings);
		return api
			.get('/orders-btc', {
				paramsSerializer: {
					serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
				},
				params: {
					page,
					size,
					...(search ? { search } : {}),
					...(clientName ? { clientName } : {}),
					...request,
				},
			})
			.then((response) => response.data)
			.then((data) => ({ ...data, body: selectOrders(data.body) }))
			.catch((error) => {
				console.log(
					`fail when trying to get ordersBtc in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	getById(id) {
		return api
			.get(`/orders-btc/${id}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectOrder)
			.catch((error) => {
				console.log(
					`fail when trying to get orderBtc with id ${id} in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	getAccountantsByClientId(clientId) {
		return api
			.get(`/orders-btc/accountants/${clientId}`)
			.then((response) => response.data)
			.catch((error) => {
				console.log(
					`fail when trying to get orderBtc accountants with client id ${clientId} in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	getByTrackingCode(orderCode) {
		return api
			.get(`/orders-btc/orderCode/${orderCode}`)
			.then((response) => response.data)
			.catch((error) => {
				console.log(
					`fail when trying to get orderBtc ${orderCode} in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	getTraceability(trackingCode) {
		return api
			.get(`/orders-btc/traceability/${encodeURIComponent(trackingCode)}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					`fail when trying to get order traceability ${trackingCode} in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error || error;
			});
	}

	// Pedido bipado + os outros pendentes do mesmo destinatário (titular ou dependente).
	getPickup(code) {
		return api
			.get(`/orders-btc/pickup/${encodeURIComponent(String(code).trim())}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					`fail when trying to get pickup for ${code} in OrderBtcService.`,
					error.response?.data?.error,
				);
				const lookupError = new Error(
					error.response?.data?.error?.message || 'Não foi possível buscar o pedido.',
				);
				lookupError.status = error.response?.status;
				throw lookupError;
			});
	}

	pickupBatch(pickupForm) {
		return api
			.post('/orders-btc/pickup/batch', pickupForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					`fail when trying to save pickup batch in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error || { message: 'Não foi possível concluir a baixa.' };
			});
	}

	createMany(orderBtcForm) {
		return api
			.post('/orders-btc/demand', orderBtcForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectOrders)
			.catch((error) => {
				console.log(
					`fail when trying to create orderBtc in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw (
					error.response?.data?.error || {
						message:
							'Não foi possível confirmar o envio. Verifique na listagem se os pedidos foram criados antes de tentar de novo.',
					}
				);
			});
	}

	scanFinalize(trackingCode, orderBtcForm) {
		return api
			.post('/orders-btc/scan/finalize', {
				...orderBtcForm,
				trackingCode,
			})
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectOrder)
			.catch((error) => {
				console.log(
					`fail when trying to finalize scanned orderBtc with tracking code ${trackingCode} in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	finalize(id, orderBtcForm) {
		return api
			.post(`/orders-btc/${id}/finalize`, orderBtcForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectOrder)
			.catch((error) => {
				console.log(
					`fail when trying to finalize orderBtc with id ${id} in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	unfullfill(id, orderBtcForm) {
		return api
			.post(`/orders-btc/${id}/unfullfill`, orderBtcForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectOrder)
			.catch((error) => {
				console.log(
					`fail when trying to unfullfill orderBtc with id ${id} in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	getPackageSizes() {
		return api
			.get('/orders-btc/package-sizes')
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					`fail when trying to get package sizes in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	hasOrderByTrackingCode(trackingCode) {
		return api
			.head(`/orders-btc/tracking-code/${trackingCode}`)
			.then((response) => true)
			.catch((error) => {
				if (error.response?.status === 404) {
					return false;
				}
				console.log(
					`fail when trying to check if has order by tracking code in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	getOrderStatuses() {
		return api
			.get('/orders-btc/statuses')
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					`fail when trying to get order statuses in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	downloadXlsx({ filters = {}, search } = {}) {
		const { clientName, ...restFilters } = filters || {};
		const request = generateSearchRequest(restFilters, {
			where: {
				id: { as: 'id', type: 'like' },
				clientId: { as: 'clientId', type: 'equal' },
				packageType: { as: 'packageType', type: 'like' },
				packageSize: { as: 'packageSize', type: 'like' },
				trackingCode: { as: 'trackingCode', type: 'like' },
				status: { as: 'status', type: 'like' },
				'status:in': { as: 'status', type: 'in' },
				baseId: { as: 'client.base.id', type: 'equal' },
				delivermanName: { as: 'deliverman.name', type: 'like' },
				'createdAt:between': { as: 'createdAt', type: 'between' },
				'deliveryDate:between': { as: 'deliveryDate', type: 'between' },
			},
		});
		return api
			.get('/orders-btc/download', {
				paramsSerializer: {
					serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
				},
				params: {
					...(clientName ? { clientName } : {}),
					...(search ? { search } : {}),
					...request,
				},
			})
			.then((response) => response.data?.body || [])
			.then((orders) => {
				const workbook = createOrderBtcWorkbook(orders);
				const workbookData = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
				const blob = new Blob([workbookData], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				});
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.setAttribute('href', url);
				link.setAttribute('download', 'pedidos-b2c-filtrados.xlsx');
				link.click();
				link.remove();
				URL.revokeObjectURL(url);
				return { totalCount: orders.length };
			})
			.catch((error) => {
				console.log(
					`fail when trying to download xlsx in OrderBtcService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}
}

const orderBtcService = new OrderBtcService();
export default orderBtcService;
